import { getLog } from '@eigtech/ui-shared-logging'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button } from '@eigtech/ui/components/button'
import { ButtonGroup } from '@eigtech/ui/components/button-group'
import { Card, CardContent } from '@eigtech/ui/components/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@eigtech/ui/components/dialog'
import { Spinner } from '@eigtech/ui/components/spinner'
import { cn } from '@eigtech/ui/lib/utils'
import {
  CheckIcon,
  FileArchiveIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  HeadphonesIcon,
  ImageIcon,
  TriangleAlert,
  UploadIcon,
  VideoIcon,
  XIcon,
  ZoomInIcon,
} from 'lucide-react'
import { memo, useEffect, useRef, useState } from 'react'
import {
  FileUploadActions,
  FileUploadState,
  formatBytes,
  useFileUpload,
} from '../hooks/use-file-upload'
import { type FileEntry } from '../types/file-entry'

const log = getLog()

export interface GalleryFileUploadProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'ref'
> {
  ref?: React.Ref<HTMLDivElement>
  files?: FileEntry[]
  maxFiles?: number
  maxSize?: number
  accept?: string
  acceptedTypesLabel?: string
  multiple?: boolean
  disabled?: boolean
  onFilesChange?: (files: FileEntry[]) => void
}

export function GalleryFileUpload({
  ref,
  files: filesProp,
  maxFiles = 10,
  maxSize = 5 * 1024 * 1024, // 5MB
  accept = 'image/*',
  acceptedTypesLabel,
  multiple = true,
  className,
  disabled,
  onFilesChange,
  ...props
}: GalleryFileUploadProps) {
  const [
    { files, isDragging, errors },
    {
      removeFile,
      clearFiles,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
    },
  ] = useFileUpload({
    files: filesProp,
    disabled,
    maxFiles,
    maxSize,
    accept,
    multiple,
    onFilesChange,
  })

  return (
    <div ref={ref} className={cn('w-full', className)}>
      {/* Upload Area */}
      <GalleryDropzone
        acceptedTypesLabel={acceptedTypesLabel}
        getInputProps={getInputProps}
        handleDragEnter={handleDragEnter}
        handleDragLeave={handleDragLeave}
        handleDragOver={handleDragOver}
        handleDrop={handleDrop}
        isDragging={isDragging}
        maxFiles={maxFiles}
        maxSize={maxSize}
        openFileDialog={openFileDialog}
        {...props}
      />

      {/* Gallery Stats */}
      <GalleryToolbar clearFiles={clearFiles} files={files} maxFiles={maxFiles} />

      {/* Image Grid */}
      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {files.map((fileItem) => (
            <GalleryItem key={fileItem.id} fileItem={fileItem} removeFile={removeFile} />
          ))}
        </div>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert className="mt-5" variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>File upload error(s)</AlertTitle>
            <AlertDescription className="[&_p]:mb-0">
              {errors.map((error, index) => (
                <p key={index} className="mbe-0 mb-0">
                  {error}
                </p>
              ))}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </div>
  )
}

export type GalleryDropzoneProps = Pick<GalleryFileUploadProps, 'acceptedTypesLabel' | 'maxFiles'> &
  Pick<FileUploadState, 'isDragging'> &
  Pick<
    FileUploadActions,
    | 'getInputProps'
    | 'handleDragEnter'
    | 'handleDragLeave'
    | 'handleDragOver'
    | 'handleDrop'
    | 'openFileDialog'
  > &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'ref'> & { maxSize: number }

export function GalleryDropzone({
  getInputProps,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  openFileDialog,
  isDragging,
  acceptedTypesLabel,
  maxSize,
  maxFiles,
  ...props
}: GalleryDropzoneProps) {
  return (
    <div
      className={cn(
        'relative rounded-lg border border-dashed p-8 text-center transition-colors',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/25 hover:border-muted-foreground/50'
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input {...getInputProps(props)} className="sr-only" />

      <div className="flex flex-col items-center gap-4">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full',
            isDragging ? 'bg-primary/10' : 'bg-muted'
          )}
        >
          <ImageIcon
            className={cn('h-5 w-5', isDragging ? 'text-primary' : 'text-muted-foreground')}
          />
        </div>

        <div className="max-w-text space-y-2">
          <h3 className="text-lg font-semibold">Upload files</h3>
          <p className="text-muted-foreground text-sm">
            Drag and drop files here or click to browse
          </p>
          <p className="text-muted-foreground text-xs">
            {acceptedTypesLabel || 'PNG, JPG, GIF'} up to {formatBytes(maxSize)} each (max{' '}
            {maxFiles} files)
          </p>
        </div>

        <Button onClick={openFileDialog}>
          <UploadIcon className="h-4 w-4" />
          Select files
        </Button>
      </div>
    </div>
  )
}

export type GalleryToolbarProps = {
  files: FileEntry[]
  maxFiles: number
  clearFiles: () => void
  onSubmit?: () => void
  submitLabel?: string
  submitButton?: React.ReactNode
}

export function GalleryToolbar({
  files,
  maxFiles,
  clearFiles,
  onSubmit,
  submitLabel,
  submitButton,
}: GalleryToolbarProps) {
  if (!files.length) {
    return null
  }
  return (
    <div className="mt-6 flex flex-col items-center justify-between gap-2 sm:flex-row">
      <div className="flex items-center gap-4">
        <h4 className="text-sm font-medium">
          Gallery ({files.length}/{maxFiles})
        </h4>
        <div className="text-muted-foreground text-xs">
          Total: {formatBytes(files.reduce((acc, file) => acc + file.size, 0))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <GalleryToolbarConvertingStatus files={files} />

        <ButtonGroup size="sm">
          <Button variant="destructive" onClick={clearFiles}>
            Clear all
          </Button>
          {submitButton
            ? submitButton
            : !!onSubmit && <Button onClick={onSubmit}>{submitLabel || 'Submit'}</Button>}
        </ButtonGroup>
      </div>
    </div>
  )
}

function GalleryToolbarConvertingStatus({ files }: { files: FileEntry[] }) {
  const convertingFiles = files.filter((file) => file.status === 'converting')
  if (convertingFiles.length === 0) {
    return null
  }

  return (
    <Alert className="w-auto" size="sm">
      <AlertIcon>
        <Spinner />
      </AlertIcon>
      <AlertContent>
        <AlertTitle>Converting {convertingFiles.length} files</AlertTitle>
      </AlertContent>
    </Alert>
  )
}

export type GalleryItemProps = {
  fileItem: FileEntry
  removeFile: (id: string) => void
  footer?: React.ReactNode
}

export function GalleryItem({ fileItem, removeFile, footer }: GalleryItemProps) {
  const isUploaded = fileItem.status === 'uploaded'
  const isError = fileItem.status === 'error'

  return (
    <Card
      key={fileItem.id}
      className={cn('gap-3 pt-0 shadow-none', isError && 'ring-destructive/50')}
    >
      <div>
        <div className="group/item relative aspect-square">
          {isUploaded ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-black/70">
              <CheckIcon className="size-10 text-white" />
              <p className="text-white">Uploaded</p>
            </div>
          ) : (
            <GalleryItemInner fileItem={fileItem} removeFile={removeFile} />
          )}
        </div>
      </div>

      {/* File Info */}
      <CardContent className="px-3">
        <div className="flex items-center justify-between">
          <div className={cn('text-xs', isError && 'text-destructive')}>
            <p className={'truncate font-semibold'}>{fileItem.name}</p>
            <p className="font-medium">{formatBytes(fileItem.size)}</p>
          </div>

          {isError && (
            <div className="text-destructive flex flex-col items-center justify-center">
              <TriangleAlert className="size-4" />
              <p className="text-sm">Error</p>
            </div>
          )}
        </div>
      </CardContent>

      {footer}
    </Card>
  )
}

function GalleryItemInner({ fileItem, removeFile }: GalleryItemProps) {
  const isUploading = fileItem.status === 'uploading'
  const isConverting = fileItem.status === 'converting'

  return (
    <>
      <div className="bg-muted relative flex h-full w-full items-center justify-center rounded-t-lg border">
        {!isConverting && getFileIcon(fileItem)}
        {fileItem.isVideo ? (
          <GalleryItemVideo fileItem={fileItem} />
        ) : fileItem.isImage ? (
          <GalleryItemImage fileItem={fileItem} />
        ) : (
          <></>
        )}
      </div>

      {/* Overlay */}
      <div className="absolute right-1 top-1 flex items-center justify-center gap-1 rounded-lg bg-black/50 p-1">
        {/* Remove Button */}
        <Button mode="icon" size="xs" variant="destructive" onClick={() => removeFile(fileItem.id)}>
          <XIcon className="opacity-100/8" />
        </Button>
      </div>

      {isUploading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-black/70 text-white">
          <Spinner className="size-10" />
          <p>Uploading...</p>
        </div>
      )}
    </>
  )
}

const GalleryItemVideo = memo(
  function GalleryItemVideo({ fileItem }: { fileItem: FileEntry }) {
    const [preview, setPreview] = useState<string | null>(null)
    const previewRef = useRef<string | null>(null)
    previewRef.current = preview

    useEffect(() => {
      const file = fileItem.getFile()
      if (!file) return

      setPreview(URL.createObjectURL(file))

      return () => {
        if (previewRef.current) {
          URL.revokeObjectURL(previewRef.current)
          setPreview(null)
        }
      }
    }, [fileItem])

    if (!preview) return null

    return (
      <video
        className="absolute inset-0 h-full w-full bg-black object-contain"
        controls
        src={preview}
      />
    )
  },
  (prevProps, nextProps) => {
    return prevProps.fileItem.id === nextProps.fileItem.id
  }
)

const GalleryItemImage = memo(
  function GalleryItemImage({ fileItem }: { fileItem: FileEntry }) {
    const [thumbnail, setThumbnail] = useState<string | null>(null)

    const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(true)
    const [isLoadingImage, setIsLoadingImage] = useState(true)

    const [isOpen, setIsOpen] = useState(false)

    const isLoading = isLoadingThumbnail || isLoadingImage

    const hasRun = useRef(false)
    const previousFileItemIdRef = useRef(fileItem?.id)

    useEffect(() => {
      const previousFileItemId = previousFileItemIdRef.current
      previousFileItemIdRef.current = fileItem?.id

      if (previousFileItemId !== previousFileItemIdRef.current) {
        hasRun.current = false
        setThumbnail(null)
      }

      if (hasRun.current || fileItem.status === 'converting') return

      hasRun.current = true

      let ignore = false

      fileItem
        .getThumbnail()
        .then((thumb) => {
          if (!ignore) setThumbnail(thumb)
        })
        .catch((error) => {
          log.error('error getting thumbnail', { error })
        })
        .finally(() => {
          if (!ignore) setIsLoadingThumbnail(false)
        })

      return () => {
        ignore = true
      }
    }, [fileItem])

    return (
      <>
        {!isLoadingThumbnail && !!thumbnail && (
          <img
            alt={fileItem.name}
            className={cn(
              'absolute inset-0 h-full w-full object-cover',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            src={thumbnail}
            onLoad={() => setIsLoadingImage(false)}
          />
        )}

        {/* View Button */}
        {thumbnail && (
          <div className="absolute left-1 top-1 flex items-center justify-center gap-1 rounded-lg bg-black/50 p-1">
            <Button
              mode="icon"
              size="xs"
              variant="secondary"
              onClick={() => {
                setIsOpen(true)
              }}
            >
              <ZoomInIcon className="opacity-100/80" />
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="bg-muted/50 absolute inset-0 flex items-center justify-center rounded-lg border">
            <Spinner className="text-muted-foreground size-6" />
          </div>
        )}

        {/* Image Preview Dialog */}
        {isOpen && thumbnail && (
          <GalleryItemDialog fileItem={fileItem} setIsOpen={setIsOpen} thumbnail={thumbnail} />
        )}
      </>
    )
  },
  (prevProps, nextProps) => {
    // only re-render if the id, name, size, or type has changed
    // do not need to re-render if the status or other properties have changed
    return (
      prevProps.fileItem.id === nextProps.fileItem.id &&
      prevProps.fileItem.name === nextProps.fileItem.name &&
      prevProps.fileItem.size === nextProps.fileItem.size &&
      prevProps.fileItem.type === nextProps.fileItem.type
    )
  }
)

function GalleryItemDialog({
  thumbnail,
  fileItem,
  setIsOpen,
}: {
  thumbnail: string
  fileItem: FileEntry
  setIsOpen: (isOpen: boolean) => void
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [noFile, setNoFile] = useState(false)

  const [url, setUrl] = useState<string | null>(null)
  const urlRef = useRef<string | null>(null)
  urlRef.current = url

  useEffect(() => {
    const file = fileItem.getFile()
    if (!file) {
      // Setting no file allows us to fallback to the thumbnail
      setNoFile(true)
      return
    }

    setUrl(URL.createObjectURL(file))

    return () => {
      // We use the ref so that useEffect doesn't have to have `url` in the dependency array
      // urlRef.current is automatically updated on each render
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        setUrl(null)
      }
    }
  }, [fileItem])

  return (
    <Dialog open onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{fileItem.name}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-center">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className="text-foreground size-8" />
            </div>
          )}

          <img
            alt="Preview"
            className={cn(
              'h-full w-auto rounded-lg object-contain transition-opacity duration-300',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            src={url ?? thumbnail}
            onLoad={() => {
              // This will trigger twice, once for the initial thumbnail, and once for the full image
              // We only want to set the loading state to false if the full image is loaded
              // OR if there is no file
              if (url || noFile) {
                setIsLoading(false)
              }
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

const getFileIcon = (file: FileEntry, className?: string) => {
  const type = file.type

  if (type.startsWith('image/'))
    return <ImageIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.startsWith('video/'))
    return <VideoIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.startsWith('audio/'))
    return <HeadphonesIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.includes('pdf'))
    return <FileTextIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.includes('word') || type.includes('doc'))
    return <FileTextIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.includes('excel') || type.includes('sheet'))
    return <FileSpreadsheetIcon className={cn('text-muted-foreground size-8', className)} />
  if (type.includes('zip') || type.includes('rar'))
    return <FileArchiveIcon className={cn('text-muted-foreground size-8', className)} />
  return <FileTextIcon />
}
